import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef } from 'react';

import { type BoxProps, getBoxStyles } from '../box/Box';

export type SkeletonSize = 'small' | 'medium' | 'large';
export type SkeletonAnimationType = 'shimmer' | 'pulse' | 'wave' | 'fade' | 'bounce' | 'flash';

const sizeSchema: Record<SkeletonSize, string> = {
  small: '8px',
  medium: '12px',
  large: '24px',
};

export type SkeletonProps = BoxProps & {
  style?: React.CSSProperties;
  size?: SkeletonSize;
  animationType?: SkeletonAnimationType;
  className?: string;
};

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ animationType = 'wave', size = 'small', h, ...props }, ref) => (
    <>
      <Component
        ref={ref}
        $animationType={animationType}
        h={h || sizeSchema[size || 'medium']}
        {...props}
      />
    </>
  ),
);

const shimmer = keyframes`
    0% {
        background-position: -200px 0;
    }
    100% {
        background-position: 200px 0;
    }
`;

const pulse = keyframes`
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
`;

const wave = keyframes`
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
`;

const fade = keyframes`
    0% {
        opacity: 0.2;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0.2;
    }
`;

const bounce = keyframes`
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-8px);
    }
`;

const flash = keyframes`
    0% {
        background-color: #bdbdbd;
    }
    50% {
        background-color: #ffffff;
    }
    100% {
        background-color: #bdbdbd;
    }
`;

type StyleProps = BoxProps & {
  $animationType?: SkeletonAnimationType;
};

const Component = styled.div<StyleProps>`
  ${getBoxStyles};

  width: ${(p) => p.w || '100%'};
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  ${({ $animationType }) => {
    if ($animationType === 'shimmer')
      return css`
        background: linear-gradient(90deg, #e0e0e0 25%, #ffffff 50%, #e0e0e0 75%);
        background-size: 200px 100%;
        animation: ${shimmer} 1.5s infinite ease-in-out;
      `;
    if ($animationType === 'pulse')
      return css`
        animation: ${pulse} 1.5s infinite ease-in-out;
      `;
    if ($animationType === 'wave')
      return css`
        &:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
          animation: ${wave} 1.5s infinite;
        }
      `;
    if ($animationType === 'fade')
      return css`
        animation: ${fade} 2s infinite ease-in-out;
      `;
    if ($animationType === 'bounce')
      return css`
        animation: ${bounce} 1s infinite ease-in-out;
      `;
    if ($animationType === 'flash')
      return css`
        animation: ${flash} 1.5s infinite ease-in-out;
      `;
    return '';
  }}
`;
